import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import API from '../lib/api';
import { useAuth } from '../context/AuthContext';
import TrustScore from '../components/TrustScore';
import ShareButtons from '../components/ShareButtons';
import { FraudBanner, FraudWarning } from '../components/FraudDisclaimer';
import { ListingSEO } from '../components/SEO';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  MapPin, Calendar, Fuel, Gauge, Car, Shield, MessageSquare, ChevronLeft,
  ChevronRight, Fingerprint, Eye, CircleDollarSign, AlertTriangle, CheckCircle,
  Star, User, Globe, Send, ShieldCheck, Loader2, CreditCard, Flag, Tag, Wrench, Package, Hash, HandCoins, Share2, Copy, Check
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ListingDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [photoIdx, setPhotoIdx] = useState(0);
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState({ avg_rating: 0, count: 0 });
  const [newReview, setNewReview] = useState({ rating: 5, comment: '' });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [initiatingEscrow, setInitiatingEscrow] = useState(false);
  const [sellerKYC, setSellerKYC] = useState({ verified: false });
  const [showReportSoldModal, setShowReportSoldModal] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportingAsSold, setReportingAsSold] = useState(false);
  const [markingSold, setMarkingSold] = useState(false);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowStickyBar(window.scrollY > 600);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    API.get(`/listings/${id}`).then(r => {
      setListing(r.data);
      setLoading(false);
      if (r.data.seller_id) {
        API.get(`/users/${r.data.seller_id}/reviews`).then(rv => {
          setReviews(rv.data.reviews || []);
          setReviewStats({ avg_rating: rv.data.avg_rating, count: rv.data.count });
        }).catch(() => {});
        API.get(`/users/${r.data.seller_id}/kyc-badge`).then(res => {
          setSellerKYC({ verified: res.data.kyc_verified });
        }).catch(() => {});
      }
    }).catch(() => { setLoading(false); });
  }, [id]);

  const formatPrice = (p) => new Intl.NumberFormat('en-GH').format(p);

  const reportAsSold = async () => {
    if (!user) { navigate('/login'); return; }
    if (!reportReason.trim()) { toast.error('Please provide a reason'); return; }
    
    setReportingAsSold(true);
    try {
      await API.post(`/listings/${listing.listing_id}/report-sold`, { reason: reportReason });
      toast.success('Report submitted for admin review');
      setShowReportSoldModal(false);
      setReportReason('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit report');
    } finally {
      setReportingAsSold(false);
    }
  };

  const markAsSold = async () => {
    if (!user || user.user_id !== listing.seller_id) return;
    if (!window.confirm('Are you sure you want to mark this listing as sold? This action cannot be undone.')) return;
    
    setMarkingSold(true);
    try {
      await API.post(`/listings/${listing.listing_id}/mark-sold`);
      toast.success('Listing marked as sold!');
      setListing(prev => ({ ...prev, status: 'sold' }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark as sold');
    } finally {
      setMarkingSold(false);
    }
  };

  const startConversation = async () => {
    if (!user) { navigate('/login'); return; }
    try {
      const res = await API.post('/conversations', { listing_id: listing.listing_id, seller_id: listing.seller_id });
      navigate(`/messages/${res.data.conversation_id}`);
    } catch (err) {
      toast.error('Could not start conversation');
    }
  };

  const startOffer = async () => {
    if (!user) { navigate('/login'); return; }
    try {
      const res = await API.post('/conversations', { listing_id: listing.listing_id, seller_id: listing.seller_id });
      navigate(`/messages/${res.data.conversation_id}?action=offer`);
    } catch (err) {
      toast.error('Could not start offer');
    }
  };

  // Build the public, crawler-friendly share URL.
  // Crawlers (WhatsApp/Twitter/Facebook) see static OG meta tags;
  // humans get JS-redirected to the SPA page.
  const shareUrl = listing
    ? `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '')}/api/share/listing/${listing.listing_id}`
    : '';
  const ogPreviewUrl = listing
    ? `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '')}/api/og/listing/${listing.listing_id}.png`
    : '';

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareCopied(true);
      toast.success('Share link copied');
      setTimeout(() => setShareCopied(false), 2000);
    } catch {
      toast.error('Could not copy. Long-press the URL above to copy manually.');
    }
  };

  const nativeShare = async () => {
    if (!navigator.share) {
      copyShareLink();
      return;
    }
    try {
      await navigator.share({
        title: listing?.title,
        text: `${listing?.title} — ${listing?.price?.toLocaleString?.() || ''} GHS on AutoTrust Ghana`,
        url: shareUrl,
      });
    } catch {
      // user dismissed; ignore
    }
  };

  const initiateEscrowPayment = async () => {
    if (!user) { navigate('/login'); return; }
    setInitiatingEscrow(true);
    try {
      const redirectUrl = `${window.location.origin}/dashboard?escrow_callback=true&listing_id=${listing.listing_id}`;
      const res = await API.post('/escrow/initiate', {
        listing_id: listing.listing_id,
        redirect_url: redirectUrl
      });
      
      if (res.data.payment_link) {
        toast.success('Redirecting to secure payment...');
        window.location.href = res.data.payment_link;
      } else {
        toast.error('Could not create payment link');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to initiate payment');
    }
    setInitiatingEscrow(false);
  };

  const submitReview = async () => {
    if (!user) { navigate('/login'); return; }
    setSubmittingReview(true);
    try {
      const res = await API.post('/reviews', {
        seller_id: listing.seller_id, listing_id: listing.listing_id,
        rating: newReview.rating, comment: newReview.comment,
      });
      setReviews(prev => [res.data, ...prev]);
      setReviewStats(prev => ({
        avg_rating: prev.count > 0 ? Math.round(((prev.avg_rating * prev.count) + newReview.rating) / (prev.count + 1) * 10) / 10 : newReview.rating,
        count: prev.count + 1,
      }));
      setNewReview({ rating: 5, comment: '' });
      toast.success('Review submitted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit review');
    }
    setSubmittingReview(false);
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!listing) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center"><h2 className="text-xl font-bold mb-2">Listing not found</h2>
        <Link to="/browse"><Button variant="outline">Back to Browse</Button></Link>
      </div>
    </div>
  );

  // Get photos - prioritize external_photos (reliable) over local photos
  const getPhotos = () => {
    // First try external_photos (Unsplash, etc.) - these are always reliable
    if (listing.external_photos?.length > 0) {
      return listing.external_photos;
    }
    // Then try photos (could be local or object store)
    if (listing.photos?.length > 0) {
      return listing.photos.map(p => {
        if (p.startsWith('http')) return p;
        return `${BACKEND_URL}${p}`;
      });
    }
    // Default placeholder
    return ['https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=800&h=600&fit=crop'];
  };
  
  const photos = getPhotos();
  const vin = listing.vin_result || {};
  const pricing = listing.pricing_validation || {};
  const nhtsa = vin.nhtsa || null;
  const sellerInfo = listing.seller_info || {};

  return (
    <main data-testid="listing-detail-page" className="min-h-screen bg-background">
      <ListingSEO listing={listing} />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-10">
        {/* Fraud Safety Banner */}
        <FraudBanner />
        
        <div className="flex items-center justify-between mb-4">
          <Button variant="ghost" className="gap-2" onClick={() => navigate(-1)} data-testid="back-btn">
            <ChevronLeft className="w-4 h-4" /> Back
          </Button>
          <ShareButtons listing={listing} />
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Photos */}
          <div className="lg:col-span-3 space-y-4">
            <div className="relative rounded-xl overflow-hidden border border-border/50 aspect-[16/10]">
              <img 
                src={photos[photoIdx]} 
                alt={listing.title} 
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = listing.listing_type === 'part' 
                    ? 'https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=800&h=600&fit=crop'
                    : 'https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=800&h=600&fit=crop';
                }}
                data-testid="listing-main-image"
              />
              {photos.length > 1 && (
                <>
                  <button className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/50 flex items-center justify-center text-white hover:bg-black/70 transition-colors"
                    onClick={() => setPhotoIdx(i => (i - 1 + photos.length) % photos.length)} data-testid="photo-prev">
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/50 flex items-center justify-center text-white hover:bg-black/70 transition-colors"
                    onClick={() => setPhotoIdx(i => (i + 1) % photos.length)} data-testid="photo-next">
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </>
              )}
              {listing.condition && (
                <Badge className="absolute top-3 left-3 bg-black/60 text-white border-none backdrop-blur-sm">{listing.condition}</Badge>
              )}
              {listing.status === 'sold' && (
                <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                  <div className="bg-red-600 text-white px-8 py-4 rounded-lg transform -rotate-12 shadow-xl">
                    <Tag className="w-8 h-8 mx-auto mb-2" />
                    <span className="text-2xl font-bold tracking-wider">SOLD</span>
                  </div>
                </div>
              )}
            </div>
            {photos.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-1">
                {photos.map((p, i) => (
                  <button key={i} onClick={() => setPhotoIdx(i)}
                    className={`flex-shrink-0 w-20 h-14 rounded-lg overflow-hidden border-2 transition-colors ${i === photoIdx ? 'border-gold' : 'border-border/50'}`}>
                    <img 
                      src={p} 
                      alt="" 
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=200&h=150&fit=crop';
                      }}
                    />
                  </button>
                ))}
              </div>
            )}

            {/* Video walkaround */}
            {Array.isArray(listing.videos) && listing.videos.length > 0 && (
              <div className="rounded-xl overflow-hidden border border-border/50 bg-black aspect-video">
                <video
                  data-testid="listing-video"
                  src={listing.videos[0].url?.startsWith('http') ? listing.videos[0].url : `${BACKEND_URL}${listing.videos[0].url}`}
                  poster={listing.videos[0].thumbnail_url
                    ? (listing.videos[0].thumbnail_url.startsWith('http') ? listing.videos[0].thumbnail_url : `${BACKEND_URL}${listing.videos[0].thumbnail_url}`)
                    : undefined}
                  controls
                  preload="metadata"
                  className="w-full h-full object-contain"
                />
              </div>
            )}
          </div>

          {/* Info Panel */}
          <div className="lg:col-span-2 space-y-6">
            <div>
              <h1 className="text-2xl font-bold mb-2" data-testid="listing-title">{listing.title}</h1>
              <p className="font-mono-display text-3xl font-bold text-gold" data-testid="listing-price">
                GHS {formatPrice(listing.price)}
              </p>
              
              {/* Seller Actions */}
              {user?.user_id === listing.seller_id && listing.status !== 'sold' && (
                <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-border/50" data-testid="seller-actions">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => navigate(`/edit-listing/${listing.listing_id}`)}
                    className="gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Edit
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={markAsSold}
                    disabled={markingSold}
                    className="gap-2 text-green-500 border-green-500/50 hover:bg-green-500/10"
                    data-testid="mark-sold-btn"
                  >
                    <CheckCircle className="w-4 h-4" />
                    {markingSold ? 'Marking...' : 'Mark as Sold'}
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={async () => {
                      if (window.confirm('Are you sure you want to delete this listing?')) {
                        try {
                          await API.delete(`/listings/${listing.listing_id}`);
                          toast.success('Listing deleted');
                          navigate('/dashboard');
                        } catch (err) {
                          toast.error('Failed to delete listing');
                        }
                      }
                    }}
                    className="gap-2 text-red-500 border-red-500/50 hover:bg-red-500/10"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete
                  </Button>
                </div>
              )}
              
              {/* Sold badge for seller */}
              {user?.user_id === listing.seller_id && listing.status === 'sold' && (
                <div className="mt-4 bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-green-500">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-semibold">This listing has been marked as sold</span>
                  </div>
                </div>
              )}
            </div>

            {/* Trust Score Card */}
            <div className="bg-card border border-border/50 rounded-xl p-5" data-testid="trust-card">
              <div className="flex items-center gap-4 mb-4">
                <TrustScore score={listing.trust_score || 0} size="lg" />
                <div>
                  <h3 className="font-semibold">Trust Score</h3>
                  <p className="text-xs text-muted-foreground">
                    {listing.listing_type === 'part' 
                      ? 'Based on images, pricing & seller verification' 
                      : 'Based on VIN, images & pricing analysis'}
                  </p>
                </div>
              </div>
              <div className="space-y-2">
                {listing.listing_type !== 'part' && (
                  <div className="flex items-center gap-2 text-sm">
                    <Fingerprint className="w-4 h-4" />
                    <span className="flex-1">VIN Check</span>
                    {vin.valid ? <CheckCircle className="w-4 h-4 text-trust-green" /> : <AlertTriangle className="w-4 h-4 text-alert-red" />}
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm">
                  <Eye className="w-4 h-4" />
                  <span className="flex-1">Image Authenticity</span>
                  <CheckCircle className="w-4 h-4 text-trust-green" />
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <CircleDollarSign className="w-4 h-4" />
                  <span className="flex-1">Price Validation</span>
                  {pricing.flag === 'fair' ? <CheckCircle className="w-4 h-4 text-trust-green" />
                    : pricing.flag === 'suspicious' ? <AlertTriangle className="w-4 h-4 text-alert-red" />
                    : <AlertTriangle className="w-4 h-4 text-gold" />}
                </div>
              </div>
            </div>

            {/* Part-specific Details */}
            {listing.listing_type === 'part' ? (
              <div className="bg-card border border-border/50 rounded-xl p-5 space-y-3">
                <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
                  <Wrench className="w-4 h-4 text-gold" /> Part Details
                </h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {listing.part_category && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Tag className="w-4 h-4" /> {listing.part_category}
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Package className="w-4 h-4" /> {listing.condition}
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <MapPin className="w-4 h-4" /> {listing.location}
                  </div>
                  {listing.brand && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Shield className="w-4 h-4" /> {listing.brand}
                    </div>
                  )}
                </div>
                
                {listing.part_number && (
                  <>
                    <Separator className="my-3" />
                    <div>
                      <span className="text-xs font-mono-display text-muted-foreground">Part Number</span>
                      <p className="font-mono-display text-sm mt-0.5">{listing.part_number}</p>
                    </div>
                  </>
                )}
                
                {/* Compatible Vehicles */}
                {listing.compatible_makes?.length > 0 && (
                  <>
                    <Separator className="my-3" />
                    <div>
                      <span className="text-xs text-muted-foreground flex items-center gap-1 mb-2">
                        <Car className="w-3 h-3" /> Compatible Vehicles
                      </span>
                      <div className="flex flex-wrap gap-1.5">
                        {listing.compatible_makes.map(make => (
                          <Badge key={make} variant="secondary" className="text-xs">
                            {make}
                          </Badge>
                        ))}
                      </div>
                      {listing.compatible_years && (
                        <p className="text-xs text-muted-foreground mt-2">
                          Years: {listing.compatible_years}
                        </p>
                      )}
                    </div>
                  </>
                )}
              </div>
            ) : (
              /* Car-specific Details - Original Specs */
              <div className="bg-card border border-border/50 rounded-xl p-5 space-y-3">
                <h3 className="font-semibold text-sm mb-3">Vehicle Details</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground"><Car className="w-4 h-4" /> {listing.make} {listing.model}</div>
                  <div className="flex items-center gap-2 text-muted-foreground"><Calendar className="w-4 h-4" /> {listing.year}</div>
                  <div className="flex items-center gap-2 text-muted-foreground"><Fuel className="w-4 h-4" /> {listing.fuel_type}</div>
                  <div className="flex items-center gap-2 text-muted-foreground"><MapPin className="w-4 h-4" /> {listing.location}</div>
                  {listing.mileage && <div className="flex items-center gap-2 text-muted-foreground"><Gauge className="w-4 h-4" /> {formatPrice(listing.mileage)} km</div>}
                  {listing.transmission && <div className="flex items-center gap-2 text-muted-foreground"><Shield className="w-4 h-4" /> {listing.transmission}</div>}
                </div>
                <Separator className="my-3" />
                {listing.vin ? (
                  <div>
                    <span className="text-xs font-mono-display text-muted-foreground">VIN</span>
                    <p className="font-mono-display text-sm mt-0.5">{listing.vin}</p>
                  </div>
                ) : (
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                    <div className="flex items-center gap-2 text-yellow-500 text-sm">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                      <span>No VIN provided - vehicle not verified</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* NHTSA Data - Only show if VIN exists and listing is a car */}
            {listing.listing_type !== 'part' && listing.vin && nhtsa && nhtsa.body_class && (
              <div className="bg-card border border-border/50 rounded-xl p-5 space-y-2" data-testid="nhtsa-data">
                <h3 className="font-semibold text-sm flex items-center gap-2">
                  <Globe className="w-4 h-4 text-gold" /> NHTSA Vehicle Report
                </h3>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {nhtsa.body_class && <div><span className="text-muted-foreground">Body:</span> <span className="font-medium">{nhtsa.body_class}</span></div>}
                  {nhtsa.engine_cylinders && <div><span className="text-muted-foreground">Engine:</span> <span className="font-medium">{nhtsa.engine_cylinders} cyl</span></div>}
                  {nhtsa.engine_displacement && <div><span className="text-muted-foreground">Displacement:</span> <span className="font-mono-display">{nhtsa.engine_displacement}L</span></div>}
                  {nhtsa.fuel_type && <div><span className="text-muted-foreground">Fuel:</span> <span className="font-medium">{nhtsa.fuel_type}</span></div>}
                  {nhtsa.drive_type && <div><span className="text-muted-foreground">Drive:</span> <span className="font-medium">{nhtsa.drive_type}</span></div>}
                  {nhtsa.plant_country && <div><span className="text-muted-foreground">Made in:</span> <span className="font-medium">{nhtsa.plant_country}</span></div>}
                  {nhtsa.doors && <div><span className="text-muted-foreground">Doors:</span> <span className="font-medium">{nhtsa.doors}</span></div>}
                  {nhtsa.vehicle_type && <div><span className="text-muted-foreground">Type:</span> <span className="font-medium">{nhtsa.vehicle_type}</span></div>}
                </div>
              </div>
            )}

            {/* Seller Info */}
            {sellerInfo.name && (
              <div className="bg-card border border-border/50 rounded-xl p-5" data-testid="seller-info">
                <h3 className="font-semibold text-sm mb-3">Seller</h3>
                <Link to={`/seller/${listing.seller_id}`} className="block hover:opacity-80 transition-opacity">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center text-gold font-bold">
                      {sellerInfo.name[0]?.toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm">{sellerInfo.name}</p>
                        {sellerKYC.verified && (
                          <Badge className="bg-trust-green/20 text-trust-green border-none gap-1 text-xs" data-testid="seller-kyc-badge">
                            <ShieldCheck className="w-3 h-3" /> Verified
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <MapPin className="w-3 h-3" /> {sellerInfo.location}
                        {sellerInfo.avg_rating > 0 && (
                          <span className="flex items-center gap-1 text-gold">
                            <Star className="w-3 h-3 fill-current" /> {sellerInfo.avg_rating} ({sellerInfo.review_count})
                          </span>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </div>
                </Link>
                {/* Seller Contact - Only show to signed-in users */}
                {user && sellerInfo.phone && (
                  <div className="mt-4 pt-4 border-t border-border/50">
                    <a 
                      href={`tel:${sellerInfo.phone}`} 
                      className="flex items-center gap-3 p-3 bg-gold/10 rounded-lg hover:bg-gold/20 transition-colors"
                      data-testid="seller-phone"
                    >
                      <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center">
                        <svg className="w-5 h-5 text-gold" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-sm">Call Seller</p>
                        <p className="text-gold font-mono-display">{sellerInfo.phone}</p>
                      </div>
                    </a>
                  </div>
                )}
                {/* Prompt to sign in to see phone */}
                {!user && sellerInfo.phone && (
                  <div className="mt-4 pt-4 border-t border-border/50">
                    <Link 
                      to="/login"
                      className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors"
                    >
                      <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                        <svg className="w-5 h-5 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-sm">Sign in to see phone number</p>
                        <p className="text-xs text-muted-foreground">Contact the seller directly</p>
                      </div>
                    </Link>
                  </div>
                )}
              </div>
            )}

            {/* Contact & Buy Actions */}
            {user?.user_id !== listing.seller_id && (
              <div className="space-y-3">
                {/* Fee Breakdown Card */}
                {listing.status !== 'sold' && listing.escrow_status !== 'in_escrow' && (
                  <div className="bg-muted/30 rounded-xl p-4 space-y-2">
                    <h4 className="text-sm font-semibold flex items-center gap-2">
                      <CreditCard className="w-4 h-4 text-gold" />
                      Payment Breakdown
                    </h4>
                    <div className="space-y-1.5 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Vehicle Price</span>
                        <span className="font-mono-display">GHS {formatPrice(listing.price)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Platform Fee (1.5%)</span>
                        <span className="font-mono-display text-yellow-500">+GHS {formatPrice(Math.round(listing.price * 0.015))}</span>
                      </div>
                      <div className="flex justify-between font-semibold border-t border-border/50 pt-1.5 mt-1.5">
                        <span>Total to Pay</span>
                        <span className="font-mono-display text-gold">GHS {formatPrice(Math.round(listing.price * 1.015))}</span>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      Funds are held securely until you confirm delivery
                    </p>
                  </div>
                )}
                
                {/* Escrow Buy Button */}
                <Button 
                  onClick={initiateEscrowPayment} 
                  disabled={initiatingEscrow || listing.escrow_status === 'in_escrow' || listing.status === 'sold'}
                  className="w-full h-12 bg-trust-green hover:bg-trust-green/90 text-white font-semibold gap-2" 
                  data-testid="buy-with-escrow-btn"
                >
                  {initiatingEscrow ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <ShieldCheck className="w-4 h-4" />
                  )}
                  {listing.status === 'sold' ? 'Sold' : 
                   listing.escrow_status === 'in_escrow' ? 'In Escrow' : 
                   initiatingEscrow ? 'Processing...' : 'Buy with Secure Escrow'}
                </Button>
                
                {/* Payment Safety Warning */}
                <FraudWarning type="payment" />

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-border" />
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="bg-card px-2 text-muted-foreground">or</span>
                  </div>
                </div>

                {/* Contact Seller Button */}
                <Button 
                  onClick={startConversation} 
                  variant="outline"
                  className="w-full h-11 font-semibold gap-2" 
                  data-testid="contact-seller-btn"
                >
                  <MessageSquare className="w-4 h-4" /> Contact Seller
                </Button>

                {/* Make Offer Button */}
                {user && user.user_id !== listing.seller_id && (
                  <Button
                    onClick={startOffer}
                    variant="outline"
                    className="w-full h-11 font-semibold gap-2 border-amber-400/60 text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950/30"
                    data-testid="make-offer-btn"
                  >
                    <HandCoins className="w-4 h-4" /> Make an Offer
                  </Button>
                )}

                {/* Share */}
                <Button
                  onClick={() => setShowShareModal(true)}
                  variant="ghost"
                  className="w-full h-11 font-medium gap-2 text-muted-foreground hover:text-foreground"
                  data-testid="share-listing-btn"
                >
                  <Share2 className="w-4 h-4" /> Share this listing
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Description */}
        {listing.description && (
          <div className="mt-8">
            <h3 className="font-semibold mb-3">Description</h3>
            <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">{listing.description}</p>
          </div>
        )}

        {/* Reviews Section */}
        <div className="mt-12" data-testid="reviews-section">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-bold">Seller Reviews</h3>
              {reviewStats.count > 0 && (
                <div className="flex items-center gap-2 mt-1">
                  <div className="flex items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map(s => (
                      <Star key={s} className={`w-4 h-4 ${s <= Math.round(reviewStats.avg_rating) ? 'text-gold fill-current' : 'text-muted-foreground'}`} />
                    ))}
                  </div>
                  <span className="text-sm font-mono-display font-semibold">{reviewStats.avg_rating}</span>
                  <span className="text-sm text-muted-foreground">({reviewStats.count} reviews)</span>
                </div>
              )}
            </div>
          </div>

          {/* Write Review */}
          {user && user.user_id !== listing.seller_id && (
            <div className="bg-card border border-border/50 rounded-xl p-5 mb-6" data-testid="write-review">
              <h4 className="font-semibold text-sm mb-3">Write a Review</h4>
              <div className="flex gap-1 mb-3">
                {[1, 2, 3, 4, 5].map(s => (
                  <button key={s} onClick={() => setNewReview(r => ({ ...r, rating: s }))} data-testid={`star-${s}`}>
                    <Star className={`w-6 h-6 transition-colors ${s <= newReview.rating ? 'text-gold fill-current' : 'text-muted-foreground hover:text-gold/50'}`} />
                  </button>
                ))}
              </div>
              <Textarea placeholder="Share your experience with this seller..." value={newReview.comment}
                onChange={(e) => setNewReview(r => ({ ...r, comment: e.target.value }))}
                rows={3} className="mb-3" data-testid="review-comment" />
              <Button onClick={submitReview} disabled={submittingReview} size="sm"
                className="bg-gold text-black hover:brightness-110 gap-2" data-testid="submit-review-btn">
                <Send className="w-3.5 h-3.5" /> {submittingReview ? 'Submitting...' : 'Submit Review'}
              </Button>
            </div>
          )}

          {/* Reviews List */}
          {reviews.length > 0 ? (
            <div className="space-y-4">
              {reviews.map(r => (
                <div key={r.review_id} className="bg-card border border-border/50 rounded-xl p-5" data-testid={`review-${r.review_id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center text-sm font-semibold">
                        {r.reviewer_name?.[0]?.toUpperCase() || 'U'}
                      </div>
                      <div>
                        <p className="text-sm font-medium">{r.reviewer_name}</p>
                        <p className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString('en-GH', { year: 'numeric', month: 'short', day: 'numeric' })}</p>
                      </div>
                    </div>
                    <div className="flex gap-0.5">
                      {[1, 2, 3, 4, 5].map(s => (
                        <Star key={s} className={`w-3.5 h-3.5 ${s <= r.rating ? 'text-gold fill-current' : 'text-muted-foreground'}`} />
                      ))}
                    </div>
                  </div>
                  {r.comment && <p className="text-sm text-muted-foreground">{r.comment}</p>}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 bg-card border border-border/50 rounded-xl">
              <User className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">No reviews yet. Be the first to review this seller.</p>
            </div>
          )}
        </div>

        {/* Report as Sold Button */}
        {listing.status === 'active' && user && user.user_id !== listing.seller_id && (
          <div className="mt-8 text-center">
            <button
              onClick={() => setShowReportSoldModal(true)}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-2"
              data-testid="report-sold-btn"
            >
              <Flag className="w-4 h-4" />
              Report this listing as sold
            </button>
          </div>
        )}
      </div>

      {/* Report as Sold Modal */}
      {showReportSoldModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
              <Flag className="w-5 h-5 text-orange-500" />
              Report Listing as Sold
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              If you believe this vehicle has already been sold, let us know. Our admins will review and remove it from active listings.
            </p>
            <Textarea
              placeholder="Why do you think this vehicle is sold? (e.g., saw it being driven, seller told you, etc.)"
              value={reportReason}
              onChange={(e) => setReportReason(e.target.value)}
              rows={3}
              className="mb-4"
              data-testid="report-reason-input"
            />
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowReportSoldModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={reportAsSold}
                disabled={reportingAsSold || !reportReason.trim()}
                className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                data-testid="submit-report-btn"
              >
                {reportingAsSold ? 'Submitting...' : 'Submit Report'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Sticky Action Bar - appears on scroll, glass-morphism */}
      <AnimatePresence>
        {showStickyBar && listing.status !== 'sold' && (
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="fixed bottom-4 left-4 right-20 md:right-auto md:left-1/2 md:-translate-x-1/2 md:max-w-2xl z-40"
            data-testid="sticky-action-bar"
          >
            <div className="flex items-center gap-3 bg-card/90 backdrop-blur-xl border border-border/60 rounded-2xl px-4 py-3 shadow-2xl">
              <div className="flex-1 min-w-0">
                <p className="text-xs text-muted-foreground truncate">{listing.title}</p>
                <p className="font-mono-display font-bold text-gold text-base leading-tight">
                  GHS {formatPrice(listing.price)}
                </p>
              </div>
              {user && user.user_id !== listing.seller_id && (
                <>
                  <Button
                    onClick={startOffer}
                    variant="outline"
                    size="sm"
                    className="rounded-full gap-1.5 h-10 px-3 border-amber-400/60 text-amber-700 dark:text-amber-300 hidden sm:inline-flex"
                    data-testid="sticky-offer-btn"
                  >
                    <HandCoins className="w-4 h-4" />
                    <span>Offer</span>
                  </Button>
                  <Button
                    onClick={startConversation}
                    variant="outline"
                    size="sm"
                    className="rounded-full gap-1.5 h-10 px-3"
                    data-testid="sticky-contact-btn"
                  >
                    <MessageSquare className="w-4 h-4" />
                    <span className="hidden sm:inline">Contact</span>
                  </Button>
                  <Button
                    onClick={initiateEscrowPayment}
                    disabled={initiatingEscrow || listing.escrow_status === 'in_escrow'}
                    size="sm"
                    className="rounded-full bg-trust-green hover:bg-trust-green/90 text-white gap-1.5 h-10 px-3"
                    data-testid="sticky-buy-btn"
                  >
                    {initiatingEscrow ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                    <span className="hidden sm:inline">Buy</span>
                  </Button>
                </>
              )}
              {(!user || user.user_id === listing.seller_id) && (
                <Button
                  onClick={() => user ? null : navigate('/login')}
                  size="sm"
                  className="rounded-full bg-gold text-black hover:brightness-110 h-10 px-4"
                  disabled={!!user}
                >
                  {user ? 'Your Listing' : 'Sign in to Buy'}
                </Button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Share Modal */}
      <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
        <DialogContent className="sm:max-w-lg" data-testid="share-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5 text-gold" />
              Share this listing
            </DialogTitle>
            <DialogDescription>
              The link below will show a rich preview on WhatsApp, Twitter, Facebook, and LinkedIn.
            </DialogDescription>
          </DialogHeader>

          {ogPreviewUrl && (
            <div className="rounded-xl overflow-hidden border border-border/50 bg-black aspect-[1200/630]">
              <img src={ogPreviewUrl} alt="Share preview" className="w-full h-full object-cover" data-testid="share-preview-img" />
            </div>
          )}

          <div className="flex items-center gap-2">
            <Input
              readOnly
              value={shareUrl}
              onFocus={(e) => e.target.select()}
              className="font-mono-display text-xs"
              data-testid="share-url-input"
            />
            <Button
              onClick={copyShareLink}
              className="bg-gold text-black hover:brightness-110 font-semibold gap-2 flex-shrink-0"
              data-testid="copy-share-link-btn"
            >
              {shareCopied ? <><Check className="w-4 h-4" /> Copied</> : <><Copy className="w-4 h-4" /> Copy</>}
            </Button>
          </div>

          <DialogFooter className="gap-2 sm:gap-2 flex-row">
            <a
              href={`https://wa.me/?text=${encodeURIComponent(`${listing?.title || 'Check out this listing'} — ${shareUrl}`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1"
              data-testid="share-whatsapp"
            >
              <Button variant="outline" className="w-full">WhatsApp</Button>
            </a>
            <a
              href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(listing?.title || '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1"
              data-testid="share-twitter"
            >
              <Button variant="outline" className="w-full">X / Twitter</Button>
            </a>
            <Button onClick={nativeShare} variant="outline" className="flex-1" data-testid="share-native">More…</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}
